import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
  CircularProgress, Stack, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import {
  inventoryApiService,
  InventoryAudit,
  InventoryItem,
  Warehouse,
} from '../../services/inventoryService';
import { useSalonContextStore } from '../../store/salonContextStore';
import { PageHeader, SectionCard, EmptyState, LoadingState, StatusPill, PillTone } from '../../components/ui';
import { countLabel, formatDate, formatNumber, toNumber } from '../../utils/format';

const NO_WAREHOUSE = 'Brak magazynu';

type ApiIssue = { field?: string; message: string };

const unitLabel = (unit?: string) => (unit === 'szt' ? 'szt.' : unit || '');

/** Ilosc bez zbednych zer: 5 → „5”, 0.125 → „0,125”. */
const formatQty = (value: unknown) => {
  const n = Math.round(toNumber(value) * 1000) / 1000;
  const decimals = Number.isInteger(n) ? 0 : (String(n).split('.')[1] || '').length;
  return formatNumber(n, decimals);
};

const AUDIT_STATUS: Record<string, { label: string; tone: PillTone }> = {
  DRAFT: { label: 'Szkic', tone: 'neutral' },
  IN_PROGRESS: { label: 'W trakcie', tone: 'plum' },
  COMPLETED: { label: 'Zakończona', tone: 'success' },
  CANCELLED: { label: 'Anulowana', tone: 'danger' },
};

const numCell = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

export default function InventoryAuditPage() {
  const navigate = useNavigate();
  const { salonId: paramSalonId } = useParams<{ salonId: string }>();
  const { activeSalonId, loadSalons } = useSalonContextStore();
  const salonId = paramSalonId || activeSalonId || '';
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null);
  const [audits, setAudits] = useState<InventoryAudit[]>([]);
  const [currentStock, setCurrentStock] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState<ApiIssue[]>([]);

  // Okno nowej inwentaryzacji
  const [createDialog, setCreateDialog] = useState(false);
  const [auditName, setAuditName] = useState('');
  const [auditNotes, setAuditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Okno spisu z natury
  const [completeDialog, setCompleteDialog] = useState(false);
  const [activeAudit, setActiveAudit] = useState<InventoryAudit | null>(null);
  const [counts, setCounts] = useState<Record<string, string>>({});

  useEffect(() => {
    loadSalons();
  }, [loadSalons]);

  const load = useCallback(async () => {
    if (!salonId) return;
    try {
      setLoading(true);
      const whs = await inventoryApiService.getWarehousesForSalon(salonId);
      if (whs.length === 0) { setError(NO_WAREHOUSE); return; }
      const wh = whs[0];
      setWarehouse(wh);
      const [auditData, stockData] = await Promise.all([
        inventoryApiService.getAudits(wh.id),
        inventoryApiService.getWarehouseStock(wh.id),
      ]);
      setAudits(auditData);
      setCurrentStock(stockData);
    } catch {
      setError('Nie udało się wczytać inwentaryzacji. Odśwież stronę.');
    } finally {
      setLoading(false);
    }
  }, [salonId]);

  useEffect(() => { load(); }, [load]);

  const clearErrors = () => { setError(''); setErrorDetails([]); };

  const readIssues = (e: any): ApiIssue[] => (Array.isArray(e?.response?.data?.details) ? e.response.data.details : []);

  const createAudit = async () => {
    if (!warehouse || !auditName) return;
    setSaving(true);
    try {
      await inventoryApiService.createAudit(warehouse.id, { name: auditName, notes: auditNotes });
      setCreateDialog(false);
      clearErrors();
      setAuditName('');
      setAuditNotes('');
      load();
    } catch (e: any) {
      const details = readIssues(e);
      setErrorDetails(details);
      setError(details.length
        ? 'Nie udało się rozpocząć inwentaryzacji — sprawdź dane w formularzu.'
        : e?.response?.data?.error || 'Nie udało się rozpocząć inwentaryzacji. Spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  };

  const openComplete = (audit: InventoryAudit) => {
    setActiveAudit(audit);
    // Na start wpisujemy stan systemowy — liczacy poprawia tylko roznice.
    const initial: Record<string, string> = {};
    currentStock.forEach(item => {
      initial[item.product.id] = String(Number(item.quantity));
    });
    setCounts(initial);
    clearErrors();
    setCompleteDialog(true);
  };

  const completeAudit = async () => {
    if (!activeAudit) return;
    setSaving(true);
    try {
      const items = currentStock.map(item => ({
        productId: item.product.id,
        countedQty: parseFloat(counts[item.product.id] || '0'),
      }));
      await inventoryApiService.completeAudit(activeAudit.id, items);
      setCompleteDialog(false);
      clearErrors();
      load();
    } catch (e: any) {
      const details = readIssues(e);
      setErrorDetails(details);
      setError(details.length
        ? 'Nie udało się zatwierdzić inwentaryzacji — sprawdź wpisane ilości.'
        : e?.response?.data?.error || 'Nie udało się zatwierdzić inwentaryzacji. Spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => {
    setAuditName(`Inwentaryzacja ${new Date().getFullYear()}`);
    setAuditNotes('');
    clearErrors();
    setCreateDialog(true);
  };

  const openAudit = audits.find(a => a.status === 'IN_PROGRESS');
  const dialogOpen = createDialog || completeDialog;
  const noWarehouse = !loading && error === NO_WAREHOUSE && (!warehouse || (!!warehouse.salonId && warehouse.salonId !== salonId));

  // Komunikaty API: `name`/`notes` trafiaja pod pole w oknie, reszta do belki bez technicznych nazw.
  const FIELD_LABELS: Record<string, string> = { name: 'Nazwa inwentaryzacji', notes: 'Uwagi' };
  const fieldError = (field: string) => (createDialog ? errorDetails.find(d => d.field === field)?.message : undefined);
  const issueLabel = (field?: string) => {
    if (!field) return '';
    if (FIELD_LABELS[field]) return `${FIELD_LABELS[field]}: `;
    const row = /^items\[(\d+)\]/.exec(field);
    const product = row ? currentStock[Number(row[1])]?.product.name : undefined;
    return product ? `${product}: ` : '';
  };

  const renderError = (inDialog: boolean) => {
    if (!error || (!inDialog && error === NO_WAREHOUSE)) return null;
    const list = inDialog && createDialog ? errorDetails.filter(d => !d.field || !FIELD_LABELS[d.field]) : errorDetails;
    return (
      <Alert severity="error" onClose={clearErrors} sx={{ mb: 2 }}>
        <Box>{error}</Box>
        {list.length > 0 && (
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {list.map((item, index) => (
              <li key={`${item.field || 'field'}-${index}`}>{issueLabel(item.field)}{item.message}</li>
            ))}
          </Box>
        )}
      </Alert>
    );
  };

  const changedCount = currentStock.filter(item => parseFloat(counts[item.product.id] || '0') !== Number(item.quantity)).length;

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Usługi i magazyn' }]}
        title="Inwentaryzacja"
        description="Spisy z natury: porównanie zliczonych ilości ze stanem w systemie i korekta różnic."
        actions={!openAudit && !noWarehouse ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={!warehouse || loading}>
            Nowa inwentaryzacja
          </Button>
        ) : undefined}
      />

      {!dialogOpen && renderError(false)}

      {openAudit && !noWarehouse && (
        <Alert
          severity="info"
          sx={{ mb: 2.5 }}
          action={
            <Button color="inherit" size="small" onClick={() => openComplete(openAudit)} sx={{ fontWeight: 700 }}>
              Przeprowadź spis
            </Button>
          }
        >
          <strong>Trwa inwentaryzacja „{openAudit.name}”.</strong> Wprowadź zliczone ilości, aby ją zakończyć.
        </Alert>
      )}

      {loading ? (
        <LoadingState variant="table" rows={3} />
      ) : noWarehouse ? (
        <SectionCard>
          <EmptyState
            icon={<WarehouseOutlinedIcon />}
            title="Ten salon nie ma jeszcze magazynu"
            description="Inwentaryzację prowadzi się dla magazynu oddziału. Dodaj oddział w sekcji „Marki i oddziały”, a jego magazyn pojawi się tutaj."
            actions={<Button variant="outlined" onClick={() => navigate('/salon/brands')}>Przejdź do marek i oddziałów</Button>}
          />
        </SectionCard>
      ) : audits.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<FactCheckOutlinedIcon />}
            title="Nie przeprowadzono jeszcze inwentaryzacji"
            description="Rozpocznij spis z natury, aby porównać faktyczne ilości produktów ze stanem w systemie."
            actions={warehouse ? (
              <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>Nowa inwentaryzacja</Button>
            ) : undefined}
          />
        </SectionCard>
      ) : (
        <SectionCard title="Historia inwentaryzacji" subtitle={countLabel(audits.length, 'spis', 'spisy', 'spisów')} padding="none">
          <TableContainer>
            <Table sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Nazwa</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Rozpoczęta</TableCell>
                  <TableCell>Zakończona</TableCell>
                  <TableCell align="right">Pozycje</TableCell>
                  <TableCell align="right" aria-label="Akcje" />
                </TableRow>
              </TableHead>
              <TableBody>
                {audits.map(audit => {
                  const st = AUDIT_STATUS[audit.status] || { label: audit.status, tone: 'neutral' as PillTone };
                  return (
                    <TableRow key={audit.id} hover>
                      <TableCell sx={{ minWidth: 180 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 'inherit' }}>{audit.name}</Typography>
                        {audit.notes && (
                          <Typography variant="caption" component="div" noWrap sx={{ maxWidth: 280 }} title={audit.notes}>{audit.notes}</Typography>
                        )}
                      </TableCell>
                      <TableCell><StatusPill label={st.label} tone={st.tone} /></TableCell>
                      <TableCell sx={numCell}>{formatDate(audit.startedAt)}</TableCell>
                      <TableCell sx={{ ...numCell, color: audit.completedAt ? 'text.primary' : 'text.secondary' }}>
                        {audit.completedAt ? formatDate(audit.completedAt) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={numCell}>{formatNumber(audit._count?.items ?? 0)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', py: 1 }}>
                        {audit.status === 'IN_PROGRESS' && (
                          <Button variant="outlined" size="small" onClick={() => openComplete(audit)}>
                            Przeprowadź spis
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
      )}

      {/* Nowa inwentaryzacja */}
      <Dialog open={createDialog} onClose={() => { setCreateDialog(false); clearErrors(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Rozpocznij inwentaryzację</DialogTitle>
        <DialogContent>
          {createDialog && renderError(true)}
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              fullWidth required label="Nazwa inwentaryzacji" value={auditName}
              onChange={e => setAuditName(e.target.value)}
              error={!!fieldError('name')}
              helperText={fieldError('name') || 'Np. rok albo miesiąc spisu — ułatwi odnalezienie go w historii.'}
            />
            <TextField
              fullWidth label="Uwagi (opcjonalnie)" value={auditNotes} multiline rows={3}
              onChange={e => setAuditNotes(e.target.value)}
              error={!!fieldError('notes')} helperText={fieldError('notes')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => { setCreateDialog(false); clearErrors(); }}>Anuluj</Button>
          <Button
            variant="contained" onClick={createAudit} disabled={saving || !auditName}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Rozpocznij inwentaryzację
          </Button>
        </DialogActions>
      </Dialog>

      {/* Spis z natury */}
      <Dialog open={completeDialog} onClose={() => { setCompleteDialog(false); clearErrors(); }} maxWidth="md" fullWidth>
        <DialogTitle>
          Spis z natury
          {activeAudit && (
            <Typography component="div" sx={{ color: 'text.secondary', fontSize: '0.9375rem', fontWeight: 500, mt: 0.25 }}>
              {activeAudit.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {completeDialog && renderError(true)}
          <Typography sx={{ color: 'text.secondary', mb: 2 }}>
            Wpisz faktycznie zliczone ilości. Po zatwierdzeniu różnice zostaną automatycznie skorygowane w stanach magazynowych.
          </Typography>
          {currentStock.length === 0 ? (
            <EmptyState
              compact
              icon={<FactCheckOutlinedIcon />}
              title="Magazyn nie ma jeszcze pozycji"
              description="Przyjmij towar do magazynu, aby było co liczyć."
            />
          ) : (
            <TableContainer>
              <Table sx={{ minWidth: 560 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Produkt</TableCell>
                    <TableCell align="right">Stan w systemie</TableCell>
                    <TableCell align="right">Zliczona ilość</TableCell>
                    <TableCell align="right">Różnica</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentStock.map(item => {
                    const counted = parseFloat(counts[item.product.id] || '0');
                    const expected = Number(item.quantity);
                    const diff = counted - expected;
                    const unit = unitLabel(item.product.unit);
                    return (
                      <TableRow key={item.id}>
                        <TableCell sx={{ fontWeight: 600 }}>{item.product.name}</TableCell>
                        <TableCell align="right" sx={numCell}>{formatQty(expected)} {unit}</TableCell>
                        <TableCell align="right" sx={{ width: 170, py: 1 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={counts[item.product.id] || '0'}
                            onChange={e => setCounts(c => ({ ...c, [item.product.id]: e.target.value }))}
                            inputProps={{ min: 0, step: 0.001, style: { textAlign: 'right' }, 'aria-label': `Zliczona ilość: ${item.product.name}` }}
                            InputProps={{ endAdornment: <InputAdornment position="end">{unit}</InputAdornment> }}
                            sx={{ width: 150, '& input': { fontVariantNumeric: 'tabular-nums' } }}
                          />
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ ...numCell, fontWeight: 700, color: diff > 0 ? 'success.main' : diff < 0 ? 'error.main' : 'text.secondary' }}
                        >
                          {diff > 0 ? '+' : diff < 0 ? '−' : ''}{formatQty(Math.abs(diff))}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {currentStock.length > 0 && (
            <Typography variant="caption" component="div" sx={{ mt: 1.25 }}>
              {changedCount === 0
                ? 'Wszystkie ilości zgadzają się ze stanem w systemie.'
                : `${countLabel(changedCount, 'pozycja', 'pozycje', 'pozycji')} z różnicą do skorygowania.`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => { setCompleteDialog(false); clearErrors(); }}>Anuluj</Button>
          <Button
            variant="contained" onClick={completeAudit} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Zatwierdź inwentaryzację
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
